import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useMediaLink, { useMediaLinkForm } from '@hooks/useMediaLink'

import { linkTypes } from '@functions/mediaLink'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditMediaLinkModal'

type ModalCallbacks = {
  closeModal: () => void,
  createMediaLink: (payload: HandleCallbackActionParams) => void,
  updateMediaLink: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  permittedLinkTypes: string[],
  showModal: boolean,
}

const CreateOrEditMediaLinkModal = (props: ModalProps) => {
  const { callbacks, showModal, permittedLinkTypes } = props
  const { closeModal, createMediaLink, updateMediaLink } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const mediaLink = digObject(modalPayload, 'mediaLink', {})
  const { linkable_type } = mediaLink

  const newRecord = !mediaLink.id

  const {
    callbacks: {
      createMediaLink: createFn,
      updateMediaLink: updateFn,
    },
    creating,
    updating,
  } = useMediaLink(mediaLink)

  const {
    entityState,
    entityState: {
      label,
      link_type,
      url,
    },
    errors,
    handlers,
    saveEnabled,
  } = useMediaLinkForm(mediaLink, { validateOn: 'change' })

  const filteredLinkTypes = linkTypes.filter(linkType => permittedLinkTypes.includes(linkType.key))

  const mediaOrExternalText = linkable_type && linkable_type === 'Campaign' ? 'External Link' : 'Media Link'

  const createOrUpdateMediaLinkPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `${mediaOrExternalText} ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine={mediaOrExternalText}
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Link Type" errorMessage={errors.link_type}>
            {/*
             // @ts-expect-error TS2322 */}
            <select name="link_type" value={link_type} {...handlers}>
              <option value="">Please Select</option>
              {filteredLinkTypes.map(linkType => (
                <option key={linkType.key} value={linkType.key}>
                  {linkType.title}
                </option>
              ))}
            </select>
          </Form.Field>

          {link_type === 'other' && (
            <Form.Field label="Label" marginTop="large">
              <input
                name="label"
                type="text"
                value={label}
                {...handlers}
              />
            </Form.Field>
          )}

          <Form.Field label="* URL" errorMessage={errors.url} marginTop="large">
            <input
              name="url"
              placeholder="http://www.realhub.com.au"
              type="text"
              value={url}
              {...handlers}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createMediaLink(createOrUpdateMediaLinkPayload)
              : () => updateMediaLink(createOrUpdateMediaLinkPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} ${mediaOrExternalText}` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditMediaLinkModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
